"use client"

import * as React from "react"

import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Label,
  Button,
  Badge,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../DisneyUi"
import { Star, Castle, Globe, Camera, TreePine, Users, TrendingUp, Loader2 } from "lucide-react"
import { saveUserRatings, getAllUserRatings, getAttractionStats, getCommentsByPark, type UserRating } from "../../lib/firebase-service"
import { FirebaseSetupInstructions } from "../firebase-setup-instructions"

interface Attraction {
  id: string
  name: string
  type: string
}

interface Park {
  name: string
  icon: React.ReactNode
  attractions: Attraction[]
}

const disneyParks: Park[] = [
  {
    name: "Magic Kingdom",
    icon: <Castle className="h-5 w-5" />,
    attractions: [
      { id: "mk-space-mountain", name: "Space Mountain", type: "Thrill Ride" },
      { id: "mk-haunted-mansion", name: "Haunted Mansion", type: "Dark Ride" },
      { id: "mk-pirates", name: "Pirates of the Caribbean", type: "Boat Ride" },
      { id: "mk-jungle-cruise", name: "Jungle Cruise", type: "Boat Ride" },
      { id: "mk-big-thunder", name: "Big Thunder Mountain Railroad", type: "Coaster" },
      { id: "mk-splash-mountain", name: "Tiana's Bayou Adventure", type: "Log Flume" },
      { id: "mk-seven-dwarfs", name: "Seven Dwarfs Mine Train", type: "Family Coaster" },
      { id: "mk-peter-pan", name: "Peter Pan's Flight", type: "Dark Ride" },
      { id: "mk-small-world", name: "It's a Small World", type: "Boat Ride" },
      { id: "mk-buzz-lightyear", name: "Buzz Lightyear's Space Ranger Spin", type: "Interactive" },
      { id: "mk-monsters-inc", name: "Monsters, Inc. Laugh Floor", type: "Show" },
      { id: "mk-carousel", name: "Prince Charming Regal Carrousel", type: "Classic Ride" },
      { id: "mk-teacups", name: "Mad Tea Party", type: "Spinning Ride" },
      { id: "mk-dumbo", name: "Dumbo the Flying Elephant", type: "Family Ride" },
      { id: "mk-barnstormer", name: "The Barnstormer", type: "Kids Coaster" },
    ],
  },
  {
    name: "EPCOT",
    icon: <Globe className="h-5 w-5" />,
    attractions: [
      { id: "ep-guardians", name: "Guardians of the Galaxy: Cosmic Rewind", type: "Indoor Coaster" },
      { id: "ep-frozen", name: "Frozen Ever After", type: "Boat Ride" },
      { id: "ep-remy", name: "Remy's Ratatouille Adventure", type: "Trackless Ride" },
      { id: "ep-test-track", name: "Test Track", type: "High Speed" },
      { id: "ep-soarin", name: "Soarin' Around the World", type: "Flight Simulator" },
      { id: "ep-spaceship-earth", name: "Spaceship Earth", type: "Dark Ride" },
      { id: "ep-living-seas", name: "The Seas with Nemo & Friends", type: "Aquarium Ride" },
      { id: "ep-imagination", name: "Journey Into Imagination with Figment", type: "Dark Ride" },
      { id: "ep-mission-space", name: "Mission: SPACE", type: "Simulator" },
      { id: "ep-gran-fiesta", name: "Gran Fiesta Tour", type: "Boat Ride" },
      { id: "ep-american-adventure", name: "The American Adventure", type: "Audio-Animatronic Show" },
    ],
  },
  {
    name: "Hollywood Studios",
    icon: <Camera className="h-5 w-5" />,
    attractions: [
      { id: "hs-rise-resistance", name: "Star Wars: Rise of the Resistance", type: "Immersive Experience" },
      { id: "hs-millennium-falcon", name: "Millennium Falcon: Smugglers Run", type: "Simulator" },
      { id: "hs-tower-terror", name: "The Twilight Zone Tower of Terror", type: "Drop Tower" },
      { id: "hs-rock-coaster", name: "Rock 'n' Roller Coaster", type: "Launch Coaster" },
      { id: "hs-toy-story-mania", name: "Toy Story Midway Mania!", type: "3D Shooter" },
      { id: "hs-slinky-dog", name: "Slinky Dog Dash", type: "Family Coaster" },
      { id: "hs-alien-swirling", name: "Alien Swirling Saucers", type: "Spinning Ride" },
      { id: "hs-mickey-railway", name: "Mickey & Minnie's Runaway Railway", type: "Trackless Ride" },
      { id: "hs-star-tours", name: "Star Tours", type: "3D Simulator" },
      { id: "hs-muppet-vision", name: "MuppetVision 3D", type: "3D Film" },
      { id: "hs-indiana-jones", name: "Indiana Jones Epic Stunt Spectacular!", type: "Stunt Show" },
      { id: "hs-beauty-beast", name: "Beauty and the Beast Live", type: "Musical" },
    ],
  },
  {
    name: "Animal Kingdom",
    icon: <TreePine className="h-5 w-5" />,
    attractions: [
      { id: "ak-avatar-flight", name: "Avatar Flight of Passage", type: "3D Simulator" },
      { id: "ak-navi-river", name: "Na'vi River Journey", type: "Boat Ride" },
      { id: "ak-expedition-everest", name: "Expedition Everest", type: "Coaster" },
      { id: "ak-kilimanjaro", name: "Kilimanjaro Safaris", type: "Safari" },
      { id: "ak-dinosaur", name: "DINOSAUR", type: "Dark Ride" },
      { id: "ak-kali-river", name: "Kali River Rapids", type: "Rapids Ride" },
      { id: "ak-primeval-whirl", name: "TriceraTop Spin", type: "Spinning Ride" },
      { id: "ak-festival-lion-king", name: "Festival of the Lion King", type: "Stage Show" },
      { id: "ak-finding-nemo", name: "Finding Nemo - The Musical", type: "Musical" },
      { id: "ak-gorilla-trail", name: "Gorilla Falls Exploration Trail", type: "Walking Trail" },
      { id: "ak-maharajah", name: "Maharajah Jungle Trek", type: "Walking Trail" },
      { id: "ak-tree-of-life", name: "It's Tough to be a Bug!", type: "3D Film" },
    ],
  },
]

interface FormData {
  userName: string
  ratings: Record<string, number>
  parkComments?: Record<string, string> // parkName -> comment
}

const ratingLabels = {
  1: "Next Time",
  2: "If There's Time",
  3: "Can't Miss",
}

const ratingColors = {
  1: "rating-not-interested",
  2: "rating-interested",
  3: "rating-must-see",
}

export default function DisneyAPP() {
  const [submitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [allUserRatings, setAllUserRatings] = useState<UserRating[]>([])
  const [attractionStats, setAttractionStats] = useState<
    Record<string, { average: number; count: number; ratings: number[] }>
  >({})
  const [loadingStats, setLoadingStats] = useState(true)
  const [hasPermissionError, setHasPermissionError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [commentsByPark, setCommentsByPark] = useState<Record<string, Array<{userName: string, comment: string, createdAt: Date}>>>({})
  // Feature flagged out for now
  // const [customAttractions, setCustomAttractions] = useState<Record<string, string>>({}) // parkName -> newAttractionName
  // const [showAddAttraction, setShowAddAttraction] = useState<Record<string, boolean>>({})

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      userName: "",
      ratings: {},
      parkComments: {},
    },
  })

  const watchedRatings = watch("ratings")
  const userName = watch("userName")

  // Load existing data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingStats(true)
        const [ratings, stats, comments] = await Promise.all([
          getAllUserRatings(),
          getAttractionStats(),
          getCommentsByPark()
        ])
        setAllUserRatings(ratings)
        setAttractionStats(stats)
        setCommentsByPark(comments)
      } catch (error: any) {
        console.error("Error loading data:", error)

        // Don't show error for permission issues on load, just log it
        if (!error.message?.includes("permission")) {
          console.warn("Failed to load community data, continuing with local functionality only")
        }
      } finally {
        setLoadingStats(false)
      }
    }

    loadData()
  }, [])

  const onSubmit = async (data: FormData) => {
    setIsLoading(true)
    setErrorMessage(null) // Clear any previous errors
    setHasPermissionError(false)

    try {
      console.log('Starting to save ratings for user:', data.userName)

      const totals = getTotalsByRating()
      const userRatingData = {
        id: `${data.userName}-${Date.now()}`, // Generate a unique ID
        userName: data.userName,
        ratings: data.ratings,
        timestamp: Date.now(),
        totalRatings: {
          notInterested: totals[1],
          interested: totals[2],
          mustSee: totals[3],
        },
        createdAt: new Date(),
        parkComments: data.parkComments || {},
      }

      console.log('Attempting to save user rating data:', userRatingData)
      await saveUserRatings(userRatingData)
      console.log('Successfully saved ratings to Firebase')

      // Refresh data after saving
      console.log('Refreshing data from Firebase...')
      const [ratings, stats, comments] = await Promise.all([
        getAllUserRatings(),
        getAttractionStats(),
        getCommentsByPark()
      ])
      setAllUserRatings(ratings)
      setAttractionStats(stats)
      setCommentsByPark(comments)
      console.log('Successfully refreshed data from Firebase')

      setSubmitted(true)
    } catch (error: any) {
      console.error("Error saving ratings:", error)

      // Determine error type and set appropriate state
      if (error.message?.includes("permission") || error.message?.includes("Permission")) {
        setHasPermissionError(true)
        setErrorMessage("Permission denied. Please check your Firebase security rules.")
      } else if (error.message?.includes("network") || error.message?.includes("Network")) {
        setErrorMessage("Network error. Please check your internet connection and try again.")
      } else if (error.message?.includes("quota") || error.message?.includes("Quota")) {
        setErrorMessage("Firebase quota exceeded. Please try again later.")
      } else {
        setErrorMessage(`Failed to save ratings: ${error.message || 'Unknown error occurred'}`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getRatingBadge = (attractionId: string) => {
    const rating = watchedRatings?.[attractionId]
    if (!rating) return null

    return (
      <Badge variant="outline" className={`ml-2 ${ratingColors[rating as keyof typeof ratingColors]}`}>
        <Star className="h-3 w-3 mr-1 fill-current" />
        {rating} - {ratingLabels[rating as keyof typeof ratingLabels]}
      </Badge>
    )
  }

  const getAttractionStatsBadge = (attractionId: string) => {
    const stats = attractionStats[attractionId]
    if (!stats || stats.count === 0) return null

    const avgRating = Math.round(stats.average * 10) / 10
    const color = avgRating >= 2.5 ? "text-green-600" : avgRating >= 1.5 ? "text-yellow-600" : "text-red-600"

    return (
      <div className="text-sm text-gray-500 mt-1">
        <span className={`font-medium ${color}`}>{avgRating.toFixed(1)} avg</span>
        <span className="ml-1">({stats.count} ratings)</span>
      </div>
    )
  }

  const getTotalsByRating = () => {
    const totals = { 1: 0, 2: 0, 3: 0 }
    Object.values(watchedRatings || {}).forEach((rating) => {
      if (rating >= 1 && rating <= 3) {
        totals[rating as keyof typeof totals]++
      }
    })
    return totals
  }

  // Feature flagged out - Custom attraction functionality
  /*
  const handleAddCustomAttraction = (parkName: string, attractionName: string) => {
    if (!attractionName.trim()) return

    // Create a unique ID for the custom attraction
    const customId = `custom-${parkName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`

    // Find the park and add the new attraction
    const parkIndex = disneyParks.findIndex(park => park.name === parkName)
    if (parkIndex !== -1) {
      const newAttraction = {
        id: customId,
        name: attractionName.trim(),
        type: "Custom Addition"
      }

      // Add to the park's attractions list
      disneyParks[parkIndex].attractions.push(newAttraction)

      // Clear the input and hide the form
      setCustomAttractions(prev => ({ ...prev, [parkName]: '' }))
      setShowAddAttraction(prev => ({ ...prev, [parkName]: false }))
    }
  }

  const toggleAddAttractionForm = (parkName: string) => {
    setShowAddAttraction(prev => ({
      ...prev,
      [parkName]: !prev[parkName]
    }))
    // Clear any existing text when toggling
    if (!showAddAttraction[parkName]) {
      setCustomAttractions(prev => ({ ...prev, [parkName]: '' }))
    }
  }
  */

  const totals = getTotalsByRating()

  const getTopRatedAttractions = () => {
    const attractions = disneyParks.flatMap((park) =>
      park.attractions.map((attraction) => ({
        ...attraction,
        parkName: park.name,
        stats: attractionStats[attraction.id],
      })),
    )

    return attractions
      .filter((attraction) => attraction.stats && attraction.stats.count >= 3)
      .sort((a, b) => (b.stats?.average || 0) - (a.stats?.average || 0))
      .slice(0, 10)
  }

  if (submitted) {
    return (
      <div className="min-h-screen container">
        <div className="max-w-4xl mx-auto">
          <Card className="text-center">
            <CardHeader>
              <CardTitle className="text-3xl text-green-600">Thank You, {userName}!</CardTitle>
              <CardDescription className="text-lg">
                Your Disney World attraction preferences have been saved to our database!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
                <div className="text-center p-3 bg-red-50 rounded-lg border">
                  <div className="text-2xl font-bold text-red-600">{totals[1]}</div>
                  <div className="text-sm text-gray-600">Next Time</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg border">
                  <div className="text-2xl font-bold text-yellow-600">{totals[2]}</div>
                  <div className="text-sm text-gray-600">If There's Time</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg border">
                  <div className="text-2xl font-bold text-green-600">{totals[3]}</div>
                  <div className="text-sm text-gray-600">Can't Miss</div>
                </div>
              </div>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  Edit Ratings
                </Button>
                <Button onClick={() => window.location.reload()} variant="default">
                  Start New Rating
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen container">
      <div className="max-w-4xl mx-auto">
        <Card className="mb-6">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold gradient-text">Disney World Attraction Rater</CardTitle>
            <CardDescription className="text-lg">
              Rate each attraction from 1-3 to plan your perfect Disney vacation!
            </CardDescription>
          </CardHeader>
        </Card>

        {hasPermissionError && <FirebaseSetupInstructions />}

        {errorMessage && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-800">
                <span className="text-lg">⚠️</span>
                <div>
                  <h4 className="font-medium">Error</h4>
                  <p className="text-sm mt-1">{errorMessage}</p>
                  <button
                    onClick={() => setErrorMessage(null)}
                    className="text-sm underline mt-2 hover:no-underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="rate" className="space-y-6">
          <TabsList className="tabs-list-grid-3">
            <TabsTrigger value="rate">Rate Attractions</TabsTrigger>
            <TabsTrigger value="community">Community Ratings</TabsTrigger>
            <TabsTrigger value="top">Top Rated</TabsTrigger>
          </TabsList>

          <TabsContent value="rate" className="space-y-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Your Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label htmlFor="userName">Your Name</Label>
                    <Input
                      id="userName"
                      placeholder="Enter your name"
                      {...register("userName", { required: "Name is required" })}
                    />
                    {errors.userName && <p className="text-sm text-red-600">{errors.userName.message}</p>}
                  </div>
                </CardContent>
              </Card>

              {userName && (
                <Card>
                  <CardHeader>
                    <CardTitle>Your Rating Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-red-50 rounded-lg border">
                        <div className="text-xl font-bold text-red-600">{totals[1]}</div>
                        <div className="text-sm text-gray-600">Next Time</div>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-lg border">
                        <div className="text-xl font-bold text-yellow-600">{totals[2]}</div>
                        <div className="text-sm text-gray-600">If There's Time</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg border">
                        <div className="text-xl font-bold text-green-600">{totals[3]}</div>
                        <div className="text-sm text-gray-600">Can't Miss</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {disneyParks.map((park) => (
                <Card key={park.name}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {park.icon}
                      {park.name}
                    </CardTitle>
                    <CardDescription>
                      Rate each attraction: 1 = Next Time, 2 = If There's Time, 3 = Can't Miss
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {park.attractions.map((attraction, attractionIndex) => (
                        <div key={attraction.id}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="font-medium">{attraction.name}</h4>
                                {getRatingBadge(attraction.id)}
                              </div>
                              <p className="text-sm text-gray-600">{attraction.type}</p>
                              {!loadingStats && getAttractionStatsBadge(attraction.id)}
                            </div>
                            <div className="flex gap-2 ml-4">
                              {[1, 2, 3].map((rating) => (
                                <label key={rating} className="cursor-pointer">
                                  <input
                                    type="radio"
                                    value={rating}
                                    {...register(`ratings.${attraction.id}`)}
                                    className="sr-only"
                                  />
                                  <div
                                    className={`
                                    w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all
                                    ${
                                      watchedRatings?.[attraction.id] === rating
                                        ? ratingColors[rating as keyof typeof ratingColors]
                                        : "border-gray-300 hover:border-gray-400"
                                    }
                                  `}
                                  >
                                    {rating}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          {attractionIndex < park.attractions.length - 1 && <Separator className="mt-4" />}
                        </div>
                      ))}

                      {/* Park Comments Section */}
                      <Separator className="mt-6" />
                      <div className="mt-4">
                        <Label htmlFor={`park-comments-${park.name}`} className="text-sm font-medium">
                          Comments about {park.name} (optional)
                        </Label>
                        <textarea
                          id={`park-comments-${park.name}`}
                          {...register(`parkComments.${park.name}`)}
                          placeholder={`Share your thoughts about ${park.name}, favorite attractions, tips, or memories...`}
                          className="input mt-2 min-h-[80px] resize-y"
                          rows={3}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}



              <div>
                <Card>
                  <CardContent className="pt-6">
                    <Button type="submit" className="w-full" size="lg" disabled={!userName || isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving to Firebase...
                        </>
                      ) : (
                        "Save My Disney Preferences"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="community" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Community Ratings ({allUserRatings.length} users)
                </CardTitle>
                <CardDescription>See what other Disney fans think about each attraction</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    Loading community data...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allUserRatings.slice(0, 10).map((rating) => (
                      <div key={rating.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{rating.userName}</h4>
                          <span className="text-sm text-gray-500">{rating.createdAt.toLocaleDateString()}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-center">
                            <div className="font-bold text-red-600">{rating.totalRatings.notInterested}</div>
                            <div className="text-gray-600">Next Time</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-yellow-600">{rating.totalRatings.interested}</div>
                            <div className="text-gray-600">If There's Time</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-green-600">{rating.totalRatings.mustSee}</div>
                            <div className="text-gray-600">Can't Miss</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comments by Park Section */}
            {Object.keys(commentsByPark).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Community Comments by Park
                  </CardTitle>
                  <CardDescription>
                    What visitors are saying about each Disney park
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {disneyParks.map(park => {
                      const parkComments = commentsByPark[park.name] || []
                      if (parkComments.length === 0) return null

                      return (
                        <div key={park.name}>
                          <div className="flex items-center gap-2 mb-4">
                            {park.icon}
                            <h3 className="font-medium text-lg">{park.name}</h3>
                            <span className="text-sm text-gray-500">({parkComments.length} comments)</span>
                          </div>
                          <div className="space-y-3">
                            {parkComments.slice(0, 5).map((comment, index) => (
                              <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm">{comment.userName}</span>
                                  <span className="text-xs text-gray-500">
                                    {comment.createdAt.toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 italic">"{comment.comment}"</p>
                              </div>
                            ))}
                            {parkComments.length > 5 && (
                              <p className="text-sm text-gray-500 text-center">
                                ... and {parkComments.length - 5} more comments
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="top" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Top Rated Attractions
                </CardTitle>
                <CardDescription>
                  Most popular attractions based on community ratings (minimum 3 ratings)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    Calculating top attractions...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getTopRatedAttractions().map((attraction, index) => (
                      <div key={attraction.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full gradient-yellow-orange flex items-center justify-center text-white font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium">{attraction.name}</h4>
                            <p className="text-sm text-gray-600">
                              {attraction.parkName} • {attraction.type}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="font-bold">{attraction.stats?.average.toFixed(1)}</span>
                          </div>
                          <p className="text-sm text-gray-500">{attraction.stats?.count} ratings</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
